/**
 * @fileoverview Event inactive message component.
 * @summary Displays message when event is not active (draft/completed/cancelled).
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/EventInactiveMessage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { FC } from 'react'

interface EventInactiveMessageProps {
  eventName: string
  status: string
}

/**
 * @summary Event inactive message component.
 * @param {EventInactiveMessageProps} props - Component props.
 * @returns {ReactElement} Event inactive message component.
 */
const EventInactiveMessage: FC<EventInactiveMessageProps> = ({ eventName, status }) => {
  const { t } = useTranslation()

  const getIcon = () => {
    switch (status) {
      case 'draft':
        return '⏳' // Hourglass for draft
      case 'completed':
        return '✅' // Check for completed
      case 'cancelled':
        return '❌' // X for cancelled
      default:
        return '⏸️' // Pause for inactive
    }
  }

  const getMessage = () => {
    switch (status) {
      case 'draft':
        return t('singer.eventNotStarted')
      case 'completed':
        return t('singer.eventEnded')
      case 'cancelled':
        return t('singer.eventCancelled')
      default:
        return t('singer.eventNotActive')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4">
      <div className="w-full page-container">
        <div className="card text-center">
          <div className="text-5xl mb-4">{getIcon()}</div>
          <h2 className="text-xl font-semibold text-(--color-text-primary) mb-4">
            {eventName}
          </h2>
          <p className="text-(--color-text-secondary) mb-6">{getMessage()}</p>
          <Link to="/" className="btn btn-primary">
            {t('common.back')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default EventInactiveMessage

